import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Building2,
  Clock,
  FileText,
  Loader2,
  Pencil,
  Save,
  Trash2,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { ReferenceIdBadge } from "@/components/ui/ReferenceIdBadge";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { AppLayout } from "@/components/layout/AppLayout";
import { ActivityHistoryDrawer } from "@/components/audit/ActivityHistoryDrawer";
import { formatDate, initials } from "@/lib/utils";
import {
  useContact,
  useDeleteContact,
  useUpdateContact,
} from "../hooks/useContacts";
import { useCompanyList } from "@/modules/companies/hooks/useCompanies";
import { useMeetingList } from "@/modules/meetings/hooks/useMeetings";
import { useAuditRecord } from "@/modules/audit/hooks/useAudit";
import { ExpensesTabContent } from "@/modules/expenses/components/ExpensesTabContent";
import { useEnquiryList } from "@/modules/enquiries/hooks/useEnquiries";
import {
  EnquiryStatusBadge,
  SourceBadge,
} from "@/modules/enquiries/components/EnquiryStatusBadge";
import type { UpdateContactPayload } from "../types";

const CARD_BORDER = "hsl(210,30%,85%)";
const TABLE_HEAD_BG = "hsl(210,30%,97%)";

type Tab = "overview" | "meetings" | "enquiries" | "expenses" | "timeline";

function StatusBadge({ status }: { status: string }) {
  const active = status === "Active";
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium"
      style={
        active
          ? { background: "hsl(142,55%,92%)", color: "hsl(142,72%,28%)" }
          : { background: "hsl(210,16%,93%)", color: "hsl(215,16%,40%)" }
      }
    >
      {status}
    </span>
  );
}

function FieldRow({
  label,
  value,
}: {
  label: string;
  value: string | null | undefined;
}) {
  return (
    <div className="space-y-1">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="text-sm text-foreground">{value || "—"}</div>
    </div>
  );
}

export default function ContactDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const companies = useCompanyList({ limit: 100 });
  const { data: contact, isLoading, isError } = useContact(id);
  const updateContact = useUpdateContact(id);
  const deleteContact = useDeleteContact();
  const enquiryCount =
    useEnquiryList({ linkedContactId: id, limit: 1 }).data?.pagination.total ??
    0;

  const [tab, setTab] = useState<Tab>("overview");
  const [editMode, setEditMode] = useState(params.get("edit") === "true");
  const [historyOpen, setHistoryOpen] = useState(false);
  const [form, setForm] = useState<UpdateContactPayload>({});
  const [deleteOpen, setDeleteOpen] = useState(false);

  useEffect(() => {
    if (contact) {
      setForm({
        firstName: contact.firstName,
        lastName: contact.lastName,
        email: contact.email ?? "",
        phone: contact.phone ?? "",
        mobile: contact.mobile ?? "",
        jobTitle: contact.jobTitle ?? "",
        department: contact.department ?? "",
        companyName: contact.companyName ?? "",
        companyId: contact.companyId ?? "",
        type: contact.type,
        status: contact.status,
        linkedIn: contact.linkedIn ?? "",
        notes: contact.notes ?? "",
      });
    }
  }, [contact]);

  function field<K extends keyof UpdateContactPayload>(
    key: K,
    value: UpdateContactPayload[K],
  ) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function handleSave() {
    await updateContact.mutateAsync({
      ...form,
      companyId: form.companyId || undefined,
    });
    setEditMode(false);
    params.delete("edit");
    setParams(params, { replace: true });
  }

  function handleCancel() {
    if (contact) {
      setForm({
        firstName: contact.firstName,
        lastName: contact.lastName,
        email: contact.email ?? "",
        phone: contact.phone ?? "",
        mobile: contact.mobile ?? "",
        jobTitle: contact.jobTitle ?? "",
        department: contact.department ?? "",
        companyName: contact.companyName ?? "",
        companyId: contact.companyId ?? "",
        type: contact.type,
        status: contact.status,
        linkedIn: contact.linkedIn ?? "",
        notes: contact.notes ?? "",
      });
    }
    setEditMode(false);
    params.delete("edit");
    setParams(params, { replace: true });
  }

  function handleDelete() {
    if (!contact) return;
    setDeleteOpen(true);
  }

  async function confirmDelete() {
    if (!contact) return;
    await deleteContact.mutateAsync(contact.id);
    navigate("/contacts");
  }

  const breadcrumb = useMemo(
    () => [
      { label: "PhazeX Hub", href: "/" },
      { label: "Business Structure" },
      { label: "Contacts", href: "/contacts" },
      { label: contact?.fullName ?? "…" },
    ],
    [contact?.fullName],
  );

  if (isLoading) {
    return (
      <AppLayout breadcrumb={breadcrumb}>
        <div className="flex items-center justify-center py-20 text-sm text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" strokeWidth={1.5} />
          Loading contact…
        </div>
      </AppLayout>
    );
  }

  if (isError || !contact) {
    return (
      <AppLayout breadcrumb={breadcrumb}>
        <div className="py-20 text-center text-sm text-muted-foreground">
          Contact not found.
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout breadcrumb={breadcrumb}>
      <div className="w-full space-y-6">
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate("/contacts")}
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
            Back to Contacts
          </button>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setHistoryOpen(true)}
            >
              <Clock className="h-4 w-4" strokeWidth={1.5} />
              History
            </Button>
            {!editMode ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => setEditMode(true)}
                >
                  <Pencil className="h-4 w-4" strokeWidth={1.5} />
                  Edit
                </Button>
                <Button variant="outline" onClick={handleDelete}>
                  <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                  Delete
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={handleCancel}>
                  <X className="h-4 w-4" strokeWidth={1.5} />
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={updateContact.isPending}>
                  {updateContact.isPending ? (
                    <Loader2
                      className="mr-1 h-4 w-4 animate-spin"
                      strokeWidth={1.5}
                    />
                  ) : (
                    <Save className="h-4 w-4" strokeWidth={1.5} />
                  )}
                  Save
                </Button>
              </>
            )}
          </div>
        </div>

        <Card style={{ borderColor: CARD_BORDER }}>
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-[hsl(210,55%,92%)] text-base font-semibold text-[hsl(210,100%,28%)]">
                {initials(contact.fullName)}
              </div>
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-3">
                  <h1 className="text-2xl font-semibold text-foreground">
                    {contact.fullName}
                  </h1>
                  <ReferenceIdBadge id={contact.code} />
                  <StatusBadge status={contact.status} />
                </div>
                {contact.companyName && (
                  <div className="mt-1 inline-flex items-center gap-1.5 text-sm text-muted-foreground">
                    <Building2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                    {contact.companyName}
                  </div>
                )}
                {contact.jobTitle && (
                  <div className="mt-1 text-sm text-muted-foreground">
                    {contact.jobTitle}
                    {contact.department ? ` · ${contact.department}` : ""}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tab bar — Vendor Expenses appears only for Vendor-type contacts */}
        <div className="border-b border-border">
          <nav className="-mb-px flex gap-4 overflow-x-auto">
            {(
              [
                { key: "overview", label: "Overview" },
                { key: "meetings", label: "Meetings" },
                { key: "enquiries", label: `Enquiries (${enquiryCount})` },
                ...(contact.type === "Vendor"
                  ? [{ key: "expenses", label: "Vendor Expenses" } as const]
                  : []),
                { key: "timeline", label: "Timeline" },
              ] as { key: Tab; label: string }[]
            ).map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={`whitespace-nowrap border-b-2 px-1 pb-3 text-sm font-medium transition-colors ${
                  tab === t.key
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                {t.label}
              </button>
            ))}
          </nav>
        </div>

        {tab === "overview" && (
          <Card style={{ borderColor: CARD_BORDER }}>
            <CardContent className="p-6 space-y-6">
              <h3 className="text-sm font-semibold text-foreground">
                Contact Information
              </h3>
              {editMode ? (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>First Name</Label>
                    <Input
                      value={form.firstName ?? ""}
                      onChange={(e) => field("firstName", e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Last Name</Label>
                    <Input
                      value={form.lastName ?? ""}
                      onChange={(e) => field("lastName", e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={form.email ?? ""}
                      onChange={(e) => field("email", e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <Input
                      value={form.phone ?? ""}
                      onChange={(e) => field("phone", e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Mobile</Label>
                    <Input
                      value={form.mobile ?? ""}
                      onChange={(e) => field("mobile", e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Job Title</Label>
                    <Input
                      value={form.jobTitle ?? ""}
                      onChange={(e) => field("jobTitle", e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Department</Label>
                    <Input
                      value={form.department ?? ""}
                      onChange={(e) => field("department", e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Company</Label>
                    <Select
                      value={form.companyId ?? ""}
                      onChange={(e) => field("companyId", e.target.value)}
                    >
                      <option value="">— None —</option>
                      {companies.data?.data.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Company Name (override)</Label>
                    <Input
                      value={form.companyName ?? ""}
                      onChange={(e) => field("companyName", e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Type</Label>
                    <Select
                      value={form.type ?? "External"}
                      onChange={(e) => field("type", e.target.value)}
                    >
                      <option value="External">External</option>
                      <option value="Partner">Partner</option>
                      <option value="Client">Client</option>
                      <option value="Vendor">Vendor</option>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Status</Label>
                    <Select
                      value={form.status ?? "Active"}
                      onChange={(e) => field("status", e.target.value)}
                    >
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </Select>
                  </div>
                  <div className="space-y-2 col-span-2">
                    <Label>LinkedIn</Label>
                    <Input
                      value={form.linkedIn ?? ""}
                      onChange={(e) => field("linkedIn", e.target.value)}
                    />
                  </div>
                  <div className="space-y-2 col-span-2">
                    <Label>Notes</Label>
                    <textarea
                      value={form.notes ?? ""}
                      onChange={(e) => field("notes", e.target.value)}
                      rows={4}
                      className="w-full rounded-md border bg-white px-3 py-2 text-sm"
                      style={{ borderColor: CARD_BORDER }}
                    />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                  <FieldRow label="First Name" value={contact.firstName} />
                  <FieldRow label="Last Name" value={contact.lastName} />
                  <FieldRow label="Email" value={contact.email} />
                  <FieldRow label="Phone" value={contact.phone} />
                  <FieldRow label="Mobile" value={contact.mobile} />
                  <FieldRow label="Job Title" value={contact.jobTitle} />
                  <FieldRow label="Department" value={contact.department} />
                  <FieldRow label="Company" value={contact.companyName} />
                  <FieldRow label="Type" value={contact.type} />
                  <FieldRow label="Status" value={contact.status} />
                  <div className="col-span-2">
                    <FieldRow label="LinkedIn" value={contact.linkedIn} />
                  </div>
                  <div className="col-span-2">
                    <FieldRow label="Notes" value={contact.notes} />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {tab === "meetings" && <ContactMeetingsTab contactId={contact.id} />}

        {tab === "enquiries" && (
          <ContactEnquiriesTab contactId={contact.id} />
        )}

        {tab === "expenses" && contact.type === "Vendor" && (
          <ExpensesTabContent vendorContactId={contact.id} />
        )}

        {tab === "timeline" && (
          <ContactTimelineTab contactId={contact.id} />
        )}

        <ActivityHistoryDrawer
          isOpen={historyOpen}
          onClose={() => setHistoryOpen(false)}
          entityType="contact"
          entityId={contact.id}
          entityLabel={contact.fullName}
        />

        <ConfirmDialog
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
          title="Delete contact?"
          description={`Are you sure you want to delete ${contact.fullName}? It will be removed from your Contacts list.`}
          onConfirm={confirmDelete}
        />
      </div>
    </AppLayout>
  );
}

// ── Meetings tab (view-only — Contacts are a reference module) ────

function ContactMeetingsTab({ contactId }: { contactId: string }) {
  const navigate = useNavigate();
  const { data, isLoading } = useMeetingList({
    attendeeContactId: contactId,
    limit: 100,
  });
  const meetings = data?.data ?? [];

  return (
    <div className="space-y-3">
      {isLoading ? (
        <div
          className="flex items-center justify-center rounded-xl border bg-card py-16 text-sm text-muted-foreground"
          style={{ borderColor: CARD_BORDER }}
        >
          <Loader2 className="mr-2 h-4 w-4 animate-spin" strokeWidth={1.5} />
          Loading…
        </div>
      ) : meetings.length === 0 ? (
        <div
          className="flex items-center justify-center rounded-xl border bg-card py-16 text-sm text-muted-foreground"
          style={{ borderColor: CARD_BORDER }}
        >
          No meetings linked to this contact yet.
        </div>
      ) : (
        <div
          className="overflow-hidden rounded-xl border bg-card"
          style={{ borderColor: CARD_BORDER }}
        >
          <table className="w-full text-sm">
            <thead
              className="text-left text-xs font-medium uppercase tracking-wide text-muted-foreground"
              style={{ background: TABLE_HEAD_BG }}
            >
              <tr>
                <th className="px-6 py-3">Title + Code</th>
                <th className="px-6 py-3">Type</th>
                <th className="px-6 py-3">Status</th>
                <th className="px-6 py-3">Scheduled</th>
                <th className="px-6 py-3">Organizer</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {meetings.map((m) => (
                <tr
                  key={m.id}
                  className="cursor-pointer transition-colors hover:bg-[hsl(210,50%,98%)]"
                  onClick={() => navigate(`/meetings/${m.id}`)}
                >
                  <td className="px-6 py-4">
                    <div className="font-medium text-foreground">{m.title}</div>
                    <div className="mt-0.5 font-mono text-xs text-muted-foreground">
                      {m.code}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">{m.type}</td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">{m.status}</td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">
                    {formatDate(m.scheduledAt)}
                  </td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">
                    {m.organizerName}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Enquiries tab (reverse-side: enquiries linked to this contact) ──

function ContactEnquiriesTab({ contactId }: { contactId: string }) {
  const navigate = useNavigate();
  const { data, isLoading } = useEnquiryList({
    linkedContactId: contactId,
    limit: 100,
  });
  const enquiries = data?.data ?? [];

  if (isLoading) {
    return (
      <div
        className="flex items-center justify-center rounded-xl border bg-card py-16 text-sm text-muted-foreground"
        style={{ borderColor: CARD_BORDER }}
      >
        <Loader2 className="mr-2 h-4 w-4 animate-spin" strokeWidth={1.5} />
        Loading…
      </div>
    );
  }

  if (enquiries.length === 0) {
    return (
      <div
        className="flex items-center justify-center rounded-xl border bg-card py-16 text-sm text-muted-foreground"
        style={{ borderColor: CARD_BORDER }}
      >
        No enquiries linked to this contact yet.
      </div>
    );
  }

  return (
    <div
      className="overflow-hidden rounded-xl border bg-card"
      style={{ borderColor: CARD_BORDER }}
    >
      <table className="w-full text-sm">
        <thead
          className="text-left text-xs font-medium uppercase tracking-wide text-muted-foreground"
          style={{ background: TABLE_HEAD_BG }}
        >
          <tr>
            <th className="px-6 py-3">Code</th>
            <th className="px-6 py-3">Source</th>
            <th className="px-6 py-3">Subject</th>
            <th className="px-6 py-3">Status</th>
            <th className="px-6 py-3">Received</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {enquiries.map((e) => (
            <tr
              key={e.id}
              className="cursor-pointer transition-colors hover:bg-[hsl(210,50%,98%)]"
              onClick={() => navigate(`/enquiries/${e.id}`)}
            >
              <td className="px-6 py-4 font-mono text-xs text-muted-foreground">
                {e.code}
              </td>
              <td className="px-6 py-4">
                <SourceBadge source={e.source} />
              </td>
              <td className="px-6 py-4 text-foreground">
                {e.subject ?? "—"}
              </td>
              <td className="px-6 py-4">
                <EnquiryStatusBadge status={e.status} />
              </td>
              <td className="px-6 py-4 text-muted-foreground">
                {formatDate(e.createdAt)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Timeline tab (audit log entries) ──────────────────────────

function ContactTimelineTab({ contactId }: { contactId: string }) {
  const { data, isLoading, isError } = useAuditRecord("contact", contactId);
  const events = data?.data ?? [];

  if (isLoading) {
    return (
      <div
        className="flex items-center justify-center rounded-xl border bg-card py-16 text-sm text-muted-foreground"
        style={{ borderColor: CARD_BORDER }}
      >
        <Loader2 className="mr-2 h-4 w-4 animate-spin" strokeWidth={1.5} />
        Loading…
      </div>
    );
  }

  if (isError) {
    return (
      <div
        className="flex items-center justify-center rounded-xl border bg-card py-16 text-sm text-destructive"
        style={{ borderColor: CARD_BORDER }}
      >
        Failed to load activity.
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div
        className="flex items-center justify-center rounded-xl border bg-card py-16 text-sm text-muted-foreground"
        style={{ borderColor: CARD_BORDER }}
      >
        No activity recorded yet.
      </div>
    );
  }

  return (
    <div className="relative ml-4 border-l-2" style={{ borderColor: CARD_BORDER }}>
      {events.map((event) => (
        <div key={event.id} className="relative mb-4 ml-6">
          <div
            className="absolute -left-[37px] flex h-6 w-6 items-center justify-center rounded-full"
            style={{
              background: "hsl(210,55%,92%)",
              color: "hsl(210,100%,28%)",
            }}
          >
            <FileText className="h-3.5 w-3.5" strokeWidth={1.5} />
          </div>
          <div
            className="rounded-xl border bg-white p-4"
            style={{ borderColor: CARD_BORDER }}
          >
            <div className="flex items-start justify-between gap-2">
              <span className="text-sm font-medium text-foreground">
                {event.action.toLowerCase().replace(/_/g, " ")}
              </span>
              <span className="whitespace-nowrap text-xs text-muted-foreground">
                {new Date(event.createdAt).toLocaleString("en-GB", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: false,
                })}
              </span>
            </div>
            {event.description && (
              <p className="mt-1.5 text-xs text-muted-foreground">
                {event.description}
              </p>
            )}
            <div className="mt-1.5 text-[11px] text-muted-foreground">
              {event.performedByName ?? "System"}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
